import { useEffect, useRef } from "react";

/**
 * Animated network/graph motif representing entity resolution across silos.
 * Pure canvas, no deps. Lightweight, responsive.
 */
const NetworkCanvas = ({ density = 60 }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = 0, h = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);

    const nodes = [];
    // Highlight nodes that pulse (entity resolution "merge" hits)
    const highlightIdxs = new Set();

    const seed = () => {
      nodes.length = 0;
      const count = Math.max(30, Math.floor((w * h) / 24000));
      for (let i = 0; i < count; i++) {
        nodes.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.18,
          vy: (Math.random() - 0.5) * 0.18,
          r: Math.random() * 1.6 + 0.6,
          hl: Math.random() < 0.08,
          phase: Math.random() * Math.PI * 2,
        });
      }
      highlightIdxs.clear();
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].hl) highlightIdxs.add(i);
      }
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    resize();
    window.addEventListener("resize", resize);

    const linkDist = 140;
    let t = 0;

    const draw = () => {
      t += 0.012;
      ctx.clearRect(0, 0, w, h);

      // Move nodes
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < -10) n.x = w + 10;
        if (n.x > w + 10) n.x = -10;
        if (n.y < -10) n.y = h + 10;
        if (n.y > h + 10) n.y = -10;
      }

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < linkDist) {
            const alpha = (1 - d / linkDist) * 0.5;
            const isHl = a.hl || b.hl;
            ctx.strokeStyle = isHl
              ? `rgba(0, 229, 255, ${alpha * 0.85})`
              : `rgba(255, 255, 255, ${alpha * 0.35})`;
            ctx.lineWidth = isHl ? 0.7 : 0.4;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      for (const n of nodes) {
        const pulse = n.hl ? (Math.sin(t * 2 + n.phase) + 1) * 0.5 : 0;
        const r = n.r + pulse * 1.6;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fillStyle = n.hl
          ? `rgba(0, 229, 255, ${0.7 + pulse * 0.3})`
          : "rgba(255,255,255,0.75)";
        ctx.fill();

        if (n.hl) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, 8 + pulse * 8, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(0, 229, 255, ${0.25 - pulse * 0.2})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      data-testid="hero-network-canvas"
      aria-hidden="true"
    />
  );
};

export default NetworkCanvas;
